import { Component } from '@theme/component';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a shared modal dialog for custom content.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The button element.
 *
 * @extends Component<Refs>
 */

export class CustomHotspotComponent extends Component {
  requiredRefs = ['trigger'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.trigger.addEventListener('click', this.handleHotspotClick);

    // Initialize View Toggle if present
    this.initViewToggle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.refs.trigger) {
      this.refs.trigger.removeEventListener('click', this.handleHotspotClick);
    }
  }

  initViewToggle() {
    // Only initialize once per section, simpler to do it here or better in a separate component?
    // Since we don't have a separate component for the section, we can check if it's already initialized.
    const wrapper = this.closest('.section-custom-hotspots');
    if (!wrapper || wrapper.dataset.viewToggleInitialized) return;

    wrapper.dataset.viewToggleInitialized = 'true';

    const toggleContainer = wrapper.querySelector('.custom-hotspots__view-toggle');
    if (!toggleContainer) return;

    const buttons = toggleContainer.querySelectorAll('.view-toggle__button');
    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const targetView = e.currentTarget.dataset.target; // view-1 or view-2
        this.switchView(wrapper, targetView);
      });
    });

    // Initial state check (usually view-1 is active by default in HTML)
    this.updateHotspotVisibility(wrapper, 'view-1');
  }

  switchView(wrapper, viewId) {
    const toggleContainer = wrapper.querySelector('.custom-hotspots__view-toggle');
    const buttons = toggleContainer.querySelectorAll('.view-toggle__button');

    // Update Toggle UI
    buttons.forEach(btn => {
      if (btn.dataset.target === viewId) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    });

    if (viewId === 'view-2') {
      toggleContainer.classList.add('view-2-active');
    } else {
      toggleContainer.classList.remove('view-2-active');
    }

    // Update Images
    const images = wrapper.querySelectorAll('.custom-hotspots__image-wrapper');
    images.forEach(img => {
      if (img.classList.contains(viewId)) {
        img.classList.remove('is-hidden');
        img.classList.add('is-active');
      } else {
        img.classList.add('is-hidden');
        img.classList.remove('is-active');
      }
    });

    // Update Hotspots
    this.updateHotspotVisibility(wrapper, viewId);
  }

  updateHotspotVisibility(wrapper, activeView) {
    // We need to query all custom-hotspot-component instances or their wrappers
    // Since we can't easily access other component instances, we rely on DOM manipulation of attributes
    // assuming the wrappers have the data-attribute.
    // Wait, in the liquid we added data-view-group to the hotspot-wrapper (div) or the component?
    // In liquid: <custom-hotspot-component ... data-view-group="..."> 
    // And I also added a wrapper in liquid <div class="hotspot-wrapper" ...> ?
    // Let's check the Liquid again. I didn't add the wrapper div in the submitted liquid patch, 
    // I added data-view-group to the component itself in custom-hotspot-item.liquid.
    
    // Oh wait, in my liquid plan I mentioned adding a wrapper `hotspot-wrapper`. 
    // BUT in the applied `custom-hotspots.liquid` update just now, I DID NOT wrap it in `hotspot-wrapper`.
    // I just render `custom-hotspot-item`. 
    // And in `custom-hotspot-item.liquid` I added `data-view-group` to the component itself.
    // So I should target `custom-hotspot-component`.

    const hotspots = wrapper.querySelectorAll('custom-hotspot-component');
    
    hotspots.forEach(hotspot => {
        const group = hotspot.dataset.viewGroup || 'all';
        if (group === 'all' || group === activeView.replace('-', '_')) {
             hotspot.style.display = '';
        } else {
             hotspot.style.display = 'none';
        }
    });
  }

  /**
   * Handle hotspot click
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    // Find the section wrapper to locate the shared modal
    const wrapper = this.closest('.section-custom-hotspots');
    if (!wrapper) return;

    // Find the shared modal and its body within the same section
    const modal = wrapper.querySelector('.custom-hotspot-shared-modal');
    if (!modal) return;
    
    const modalBody = modal.querySelector('.custom-hotspot-modal-body');
    if (!modalBody) return;

    // Find the local template containing the content for this hotspot
    const template = this.querySelector('.hotspot-content-template');
    if (!template) return;

    // Populate Content
    modalBody.innerHTML = '';
    modalBody.appendChild(template.content.cloneNode(true));

    // Open Modal
    this.openSharedModal(modal);
  };

  openSharedModal(modal) {
    modal.classList.add('is-active');
    modal.setAttribute('aria-hidden', 'false');

    // Clean up previous listeners if any (to prevent duplicates)
    if (modal._closeHandler) {
      modal.removeEventListener('click', modal._closeHandler);
      modal._closeHandler = null;
    }
    if (modal._keyHandler) {
      document.removeEventListener('keydown', modal._keyHandler);
      modal._keyHandler = null;
    }

    // Define handlers
    const closeHandler = (ev) => {
      const isBackdrop = ev.target.classList.contains('custom-hotspot-modal-backdrop');
      const isCloseBtn = ev.target.closest('.custom-hotspot-close');
      
      if (isBackdrop || isCloseBtn) {
        ev.preventDefault();
        ev.stopPropagation(); // Prevent clicks from passing through
        this.closeSharedModal(modal);
      }
    };

    const keyHandler = (ev) => {
      if (ev.key === 'Escape') {
        this.closeSharedModal(modal);
      }
    };

    // Store handlers for cleanup
    modal._closeHandler = closeHandler;
    modal._keyHandler = keyHandler;

    // Attach listeners
    modal.addEventListener('click', closeHandler);
    document.addEventListener('keydown', keyHandler);
  }

  closeSharedModal(modal) {
    modal.classList.remove('is-active');
    modal.setAttribute('aria-hidden', 'true');
    
    // Cleanup listeners
    if (modal._closeHandler) {
      modal.removeEventListener('click', modal._closeHandler);
      modal._closeHandler = null;
    }
    if (modal._keyHandler) {
      document.removeEventListener('keydown', modal._keyHandler);
      modal._keyHandler = null;
    }
    
    // Optional: Clear content after a brief delay to allow transition? 
    // For simplicity, we can leave it. The next open will clear it.
    // Clearing it immediately closes video/audio.
    const modalBody = modal.querySelector('.custom-hotspot-modal-body');
    if (modalBody) modalBody.innerHTML = '';
  }
}

// Register custom element
if (!customElements.get('custom-hotspot-component')) {
  customElements.define('custom-hotspot-component', CustomHotspotComponent);
}
